import { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { InfoButton } from './InfoModal';
import BreadcrumbTrail from './BreadcrumbTrail';
import { getBreadcrumbParents } from '../utils/routeBreadcrumbs';
import { usePageChromeOptional } from './PageChrome';
import { useNavigationOrigin } from './NavigationOrigin';

function SubPageNav({ items }) {
  if (!items?.length) return null;
  return (
    <nav
      className="flex flex-wrap items-center gap-1 sm:gap-2 text-sm min-w-0 overflow-x-auto"
      aria-label="Section navigation"
    >
      {items.map((item) => {
        const key = item.key || item.id || item.to || item.label;
        const activeClass = 'text-white font-semibold px-2.5 py-1 rounded-md bg-white/15 shrink-0';
        const idleClass =
          'text-white/65 hover:text-white px-2.5 py-1 rounded-md hover:bg-white/10 shrink-0';
        if (typeof item.onClick === 'function') {
          return (
            <button
              key={key}
              type="button"
              onClick={item.onClick}
              className={item.active ? activeClass : idleClass}
            >
              {item.label}
            </button>
          );
        }
        if (item.active) {
          return (
            <span key={key} className={activeClass}>
              {item.label}
            </span>
          );
        }
        return (
          <Link key={key} to={item.to} className={idleClass}>
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Registers page title / breadcrumb / sub-nav / tabs into the sticky Layout header.
 * Renders nothing in the page body (content starts directly below the header).
 *
 * Line 1: `ancestors < yy` (large) + Info / actions.
 * Line 2: sub-section links (building peers).
 * Line 3: tabs (`?tab=` — does not change line 1–2).
 */
export default function PageHeading({
  title,
  infoTitle,
  infoChildren,
  actions,
  className: _className,
  trail,
  subPages,
  backTo,
  backLabel,
  showBack,
  onBack,
  tabs,
  activeTab,
  onTabChange,
  meta: _meta,
  subtitle: _subtitle,
  suppressBreadcrumb: _suppressBreadcrumb,
}) {
  const chrome = usePageChromeOptional();
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const navOriginCtx = useNavigationOrigin();

  const setHasHeading = chrome?.setHasHeading;
  useLayoutEffect(() => {
    if (!setHasHeading) return undefined;
    setHasHeading(true);
    return () => setHasHeading(false);
  }, [setHasHeading]);

  const useTrailNav = trail !== undefined && trail !== null;
  const trailItems = useTrailNav ? trail : [];

  const parents = getBreadcrumbParents(pathname);
  const showInfo = infoTitle && infoChildren != null;
  const parent = parents.length > 0 ? parents[parents.length - 1] : null;
  const isDashboardParent =
    parent && (parent.to === '/' || String(parent.label || '').toLowerCase() === 'dashboard');
  const crumbParent = parent && !isDashboardParent ? parent : null;

  const trackedOrigin = locationState?.origin || navOriginCtx?.origin || null;
  const resolvedBackTo =
    (typeof onBack === 'function' ? null : trackedOrigin?.to) ||
    backTo ||
    crumbParent?.to ||
    null;
  const resolvedBackLabel =
    (typeof onBack === 'function' ? null : trackedOrigin?.label) ||
    backLabel ||
    (crumbParent ? crumbParent.label : null);

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (trackedOrigin?.to && navOriginCtx?.navigateBack) {
      navOriginCtx.navigateBack(trackedOrigin);
      return;
    }
    if (resolvedBackTo) navigate(resolvedBackTo);
  };

  // Trail nav usually replaces back; set showBack={true} to keep an in-page back (e.g. nested DB tools).
  const shouldShowBack =
    showBack !== false &&
    (showBack === true || !useTrailNav) &&
    (!!resolvedBackLabel || typeof onBack === 'function') &&
    (!!resolvedBackTo || typeof onBack === 'function');

  const tabList = Array.isArray(tabs) ? tabs : [];
  const subPageItems = Array.isArray(subPages) ? subPages : [];

  const line1Row = (
    <div className="flex flex-nowrap items-center gap-x-2 min-w-0 flex-1 h-full overflow-hidden">
      <div className="min-w-0 flex items-center gap-2 overflow-hidden">
        {useTrailNav ? (
          <BreadcrumbTrail items={trailItems} currentTitle={title} />
        ) : (
          <h1 className="text-lg sm:text-xl font-bold tracking-tight text-white min-w-0 truncate leading-7">
            {title}
          </h1>
        )}
        {showInfo && (
          <InfoButton title={infoTitle} className="text-sky-200 hover:text-white shrink-0">
            {infoChildren}
          </InfoButton>
        )}
      </div>
      {actions ? (
        <div
          className="ml-auto flex flex-nowrap items-center gap-2 shrink-0 max-w-[48%] sm:max-w-[55%] lg:max-w-none overflow-x-auto overflow-y-hidden
            [&>div]:flex [&>div]:flex-nowrap [&>div]:items-center [&>div]:gap-2 [&>div]:shrink-0
            [&_button.border]:border-white/30 [&_button.border]:text-white [&_button.border]:bg-transparent
            [&_button.border]:hover:bg-white/10
            [&_a.border]:border-white/30 [&_a.border]:text-white [&_a.border]:bg-transparent
            [&_a.border]:hover:bg-white/10
            [&_a.text-gray-500]:text-sky-200 [&_a.text-gray-500]:hover:text-white
            [&_a.text-primary-700]:text-sky-200 [&_a.text-primary-700]:hover:text-white
            [&_button]:shrink-0 [&_a]:shrink-0"
        >
          {actions}
        </div>
      ) : null}
    </div>
  );

  const showSubNavRow = useTrailNav || subPageItems.length > 0;

  // Outside Layout (no chrome): fall back to inline navy banner
  if (!chrome) {
    return (
      <header className="mb-6">
        <div className="rounded-xl overflow-hidden bg-navy-900 text-white shadow-md">
          <div className="px-4 sm:px-5 pt-4 pb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {shouldShowBack && (
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center text-white/80 hover:text-white text-sm font-medium shrink-0"
              >
                <ArrowLeft className="w-5 h-5 mr-1.5 shrink-0" />
                {resolvedBackLabel}
              </button>
            )}
            {line1Row}
          </div>
          {showSubNavRow && (
            <div className="h-10 flex items-center px-4 sm:px-5 border-t border-white/10 bg-navy-900">
              <SubPageNav items={subPageItems} />
            </div>
          )}
        </div>
      </header>
    );
  }

  void chrome.slotTick;
  const mainTarget = chrome.mainRef.current;
  const subNavTarget = chrome.subNavRef.current;
  const tabsTarget = chrome.tabsRef.current;

  if (!mainTarget || mainTarget.nodeType !== 1) {
    return null;
  }

  const main = (
    <div className="flex items-center min-w-0 flex-1 h-full gap-2 overflow-hidden">
      {shouldShowBack && (
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center text-white/80 hover:text-white text-sm font-medium shrink-0 max-w-[9rem] sm:max-w-none"
        >
          <ArrowLeft className="w-5 h-5 mr-1.5 shrink-0" />
          <span className="truncate hidden sm:inline">{resolvedBackLabel}</span>
        </button>
      )}
      {line1Row}
    </div>
  );

  const subNavNode =
    subNavTarget && subNavTarget.nodeType === 1
      ? createPortal(
          showSubNavRow ? <SubPageNav items={subPageItems} /> : null,
          subNavTarget
        )
      : null;

  const tabsNode =
    tabsTarget && tabsTarget.nodeType === 1
      ? createPortal(
          tabList.length > 0 ? (
            <div className="flex flex-wrap gap-1 px-3 lg:px-6 bg-navy-950/40 border-t border-white/10">
              {tabList.map((t) => {
                const id = t.id ?? t.key;
                const label = t.label ?? t.name;
                const isActive = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onTabChange?.(id)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      isActive
                        ? 'bg-gray-50 text-navy-900 shadow-sm'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : null,
          tabsTarget
        )
      : null;

  return (
    <>
      {createPortal(main, mainTarget)}
      {subNavNode}
      {tabsNode}
    </>
  );
}
